<template>
    <div id="clients-page">
        <div class="container-fluid">
            <h1>{{ t('quotations-report_title') }}</h1>
            <div>
                <filter-bar
                    ref="filterBar"
                    :filters="extraFilters"
                    @changed="updateFilters"
                    :has-branch-filter="canFilterPerBranch"
                    :show-labels="true" />
            </div>
            <div class="clearfix"></div>
            <div>
                <template v-if="ready">
                    <stats :stats="stats" />
                    <div class="row main-chart">
                        <div   v-show="chartData.length > 0" class="col-sm-12">
                            <quotations-income :height="218" :chart="chartData" />
                        </div>
                    </div>
                    <grid source="quotations" :filters="filters" :showSearch="false">
                        <template v-slot:grid-actions>
                                    <div>
                                        <button
                                            class="filter add-filter"
                                            @click.prevent="exportQuotations"
                                            :disabled="exporting"
                                            :title="t('export')"
                                        >
                                           {{ t('export') }} <span class="ml-2 far fa-download" />
                                        </button>

                                    </div>
                                </template>

                        <template v-slot:rows="grid">
                            <thead>
                                <tr>
                                    <grid-th :grid="grid">{{ t('id') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('date') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('expiration_date') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('status') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('value') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('client') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('phone') }}</grid-th>
                                    <grid-th :grid="grid">{{ t('email') }}</grid-th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr style="cursor: pointer" v-for="order in grid.data" :key="order.id" @click="
                                    $router.push(
                                        'client/' +
                                        order.clients_id +
                                        '#event-' +
                                        order.id,
                                    )
                                    ">
                                    <td>
                                        {{ order.id }} ({{ order.external_id }})
                                    </td>
                                    <td style="min-width: 150px">
                                        <human-date :date="order.created_at" :format="t('dates-format_date_time')" />
                                    </td>
                                    <td style="min-width: 150px">
                                        <human-date :date="order.expired_at" :format="t('dates-format_date_time')" />
                                    </td>
                                    <td>{{ order.external_status }}</td>
                                    <td>{{ order.value }}</td>
                                    <td>
                                        {{ order.first_name }}
                                        {{ order.last_name }}
                                    </td>
                                    <td style="min-width: 100px">
                                        {{ order.whatsapp }}
                                    </td>
                                    <td>{{ order.email }}</td>
                                </tr>
                            </tbody>
                        </template>
                    </grid>
                </template>
            </div>
        </div>
    </div>
</template>
<script>
import saveData from "../helpers/saveData";
import keviApi from "../keviApi.js";
import { formatInstantInPlaceTz } from "../helpers/datetime";
import QuotationsIncome from "./quotations-income.vue";
import Stats from "./stats.vue";
import Papa from "papaparse";

export default {
    data() {
        return {
            exporting: false,
            filters: {},
            ready: false,
            stats: {},
            chartData: [],
            extraFilters: [],
            pendingRun: false,
        };
    },
    computed: {
        user() {
            return this.$store.state.user;
        },
        canFilterPerBranch() {
            return !!this.user.permissions["reports"]["options"]["global_access"];
        },
    },
    async mounted() {
        const filterBar = this.$refs.filterBar;
        filterBar.addFilter("period");
        if (this.$module("segments")) {
            const segmentsRequest = await this.$http(keviApi.getOptions("segments"));
            let segments = segmentsRequest.body;
            if (segments) {
                this.extraFilters.push({
                    component: "checkbox",
                    label: this.$root.t('filter_bar-client_segments'),
                    name: "client-segments",
                    options: {
                        options: segments.map((seg) => ({
                            value: seg.id,
                            label: seg.name,
                        })),
                        icon: "fa-tag",
                    },
                    removable: true,
                });
            }
        }
        if (this.$module("quotations")) {
            const statusRequest = await this.$http(keviApi.getOptions("settings/external-quotations-status", "GET"));
            if (statusRequest.status === 200) {
                let status = statusRequest.body.status;
                this.extraFilters.push({
                    component: "checkbox",
                    label: this.$root.t('filter_bar-quotation_status'),
                    name: "quotation-status",
                    options: {
                        options: status.map((s) => ({
                            value: s.id,
                            label: s.external_label,
                        })),
                        icon: "fa-tag",
                    },
                    removable: true,
                });
            }
        }
        const fieldsRequest = await this.$http(keviApi.getOptions("/quotation-fields", "GET", {
            "only-report-fields": 1,
        }));
        for (let f in fieldsRequest.body) {
            let field = fieldsRequest.body[f];
            if (field.type === "user") {
                this.$store
                    .dispatch("placeDetails/setPlace", this.placeId)
                    .then(() => {
                        let users = this.users.filter(
                            (u) => parseInt(u.active) === 1,
                        );
                        this.extraFilters.push({
                            component: "selectbox",
                            label: field.field_name,
                            name: "f_" + field.field_identifier,
                            options: [
                                ...users.map((u) => ({
                                    value: u.id,
                                    label: u.name,
                                })),
                            ],
                            removable: true,
                        });
                    });
            } else {
                this.extraFilters.push({
                    component: "textfield",
                    label: field.field_name,
                    name: "f_" + field.field_identifier,
                    options: {
                        options: [],
                        icon: "fa-tag",
                    },
                    removable: true,
                });
            }
        }
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        updateFilters: function (filters) {
          this.filters = filters;
          if (this.filters && this.filters.period && !this.ready) {
            this.ready = true;
          }
          if (this.ready) {
            this.getStats();
            this.getChartData();
          } else {
            window.setTimeout(() => {
              this.updateFilters(filters);
            }, 300);
          }
        },
        getStats() {
            const options = keviApi.getOptions("/quotations/stats", "GET", {
                filters: this.filters,
            });
            this.ready = false;
            this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.ready = true;
                    this.stats = response.data;
                }
            });
        },
        getChartData() {
            const options = keviApi.getOptions("/quotations/absoluteChart", "GET", {
                filters: this.filters,
            });
            this.ready = false;
            this.$http(options).then((response) => {
                if (response.status === 200) {
                    this.ready = true;
                    this.chartData = response.data;
                }
            });
        },
        exportQuotations() {
            const options = keviApi.getOptions("quotations", "GET", {
                filters: this.filters,
                export: true,
            });
            this.exporting = true;

            const tz =
                (this.$store.state.place && this.$store.state.place.timezone) ||
                undefined;
            const locale =
                (this.$store.state.place && this.$store.state.place.locale) ||
                undefined;
            this.$http(options).then((response) => {
                const quotations = response.body.data.map((quote) => {
                    // i18n-validator-ignore-next-line: API field names for external system integration  
                    return {
                        "Id": quote.external_quote_id,
                        "DataCriacao": formatInstantInPlaceTz(quote.created_at, tz, locale),
                        "Valor": quote.value,
                        "DataExpiracao": formatInstantInPlaceTz(quote.expired_at, tz, locale),
                        "Status": quote.external_status,
                        "Cliente":
                            String(quote.first_name) +
                            " " +
                            String(quote.last_name),
                        "Email" : quote.email,
                        "Telefone": quote.whatsapp,
                    };
                });
                const csv = Papa.unparse(quotations);
                saveData(csv, "quotations.csv");
                this.exporting = false;
            });
        },
    },
    components: { Stats, QuotationsIncome },
};
</script>
